import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { hasPricesEditPermission } from '@paragon/login-tools';

import { useAppSelector } from '../hooks';
import { PriceActionBar } from '../PriceActionBar';
import { BulkEditControls } from './BulkEditControls';
import { PriceGridGroupRow } from './PriceGridGroupRow';
import { PriceGridHeader } from './PriceGridHeader';
import { PriceGridRow } from './PriceGridRow';
import {
  actionColumnWidth,
  ColumnData,
  DisplayRow,
  Filters,
  FilterValue,
  isRangeFilter,
  RowBase,
  RowEdits,
  selectionColumnWidth,
  Sorting,
} from './priceGridTypes';
import {
  applyEditsToRow,
  buildDisplayRows,
  filterAndSortRows,
  getRowsScopeKey,
  getUpdatedRows,
} from './priceGridUtilities';

export type { ColumnData, Sorting } from './priceGridTypes';

function isActiveFilter(filterValue: FilterValue) {
  if (Array.isArray(filterValue)) return filterValue.length > 0;
  if (typeof filterValue === 'string') return filterValue.length > 0;
  return isRangeFilter(filterValue) && Boolean(filterValue.min || filterValue.max);
}

interface Props<TRow extends RowBase> {
  readonly rows: readonly TRow[];
  readonly columns: readonly ColumnData<TRow>[];
  readonly commitRowEdits: (editedRows: readonly TRow[]) => Promise<boolean>;
  readonly defaultSorting: readonly Sorting[];
  // The material-specific insert bar opens on demand and preserves its draft if selection changes behind it.
  readonly insertBar?: (onClose: () => void, onHeightChange: (height: number) => void) => React.ReactNode;
}

interface AllRowEdits {
  readonly [rowGuid: string]: RowEdits | undefined;
}

interface BulkEdits {
  readonly [scopeKey: string]: RowEdits | undefined;
}

export function PriceGrid<TRow extends RowBase>({
  rows,
  columns,
  commitRowEdits,
  defaultSorting,
  insertBar,
}: Props<TRow>) {
  const [filters, setFilters] = useState<Filters>({});
  const [sortings, setSortings] = useState<readonly Sorting[]>(defaultSorting);
  const [allRowEdits, setAllRowEdits] = useState<AllRowEdits>({});
  const [groupByColumnKey, setGroupByColumnKey] = useState('');
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<readonly string[]>([]);
  const [bulkEdits, setBulkEdits] = useState<BulkEdits>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [selectedRowGuids, setSelectedRowGuids] = useState<ReadonlySet<string>>(new Set());
  const [lastSelectedRowGuid, setLastSelectedRowGuid] = useState<string | null>(null);
  const [isInsertBarOpen, setIsInsertBarOpen] = useState(false);
  const [actionBarHeight, setActionBarHeight] = useState(80);
  const hasEditPermission = useAppSelector(hasPricesEditPermission);

  const editableColumns = useMemo(() => columns.filter((column) => column.getUpdatedRowWithEdit), [columns]);
  const tableMinWidth = useMemo(
    () => columns.reduce((width, column) => width + column.width, selectionColumnWidth + actionColumnWidth),
    [columns],
  );
  const virtuosoTableComponents = useMemo(
    (): TableComponents<DisplayRow<TRow>> => ({
      Scroller: React.forwardRef<HTMLDivElement>((props, ref) => <TableContainer {...props} ref={ref} />),
      Table: (props) => (
        <Table
          {...props}
          style={{
            borderCollapse: 'separate',
            tableLayout: 'fixed',
            width: '100%',
            minWidth: tableMinWidth,
          }}
        />
      ),
      TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableHead {...props} ref={ref} sx={{ bgcolor: 'background.paper' }} />
      )),
      TableRow,
      TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => <TableBody {...props} ref={ref} />),
    }),
    [tableMinWidth],
  );

  const startEditingRow = useCallback((rowGuid: string) => {
    setEditError(null);
    setAllRowEdits((prevState) => ({ ...prevState, [rowGuid]: {} }));
  }, []);
  const clearEditsForRow = useCallback((rowGuid: string) => {
    setAllRowEdits((prevState) => ({ ...prevState, [rowGuid]: undefined }));
  }, []);
  const updateRowEdit = useCallback((rowGuid: string, columnKey: string, value: string) => {
    setEditError(null);
    setAllRowEdits((prevState) => ({
      ...prevState,
      [rowGuid]: { ...prevState[rowGuid], [columnKey]: value },
    }));
  }, []);

  const saveRowEdits = useCallback(
    async (row: TRow) => {
      const rowEdits = allRowEdits[row.guid];
      if (!rowEdits) {
        console.error(`Expected edits to exist for row ${row.guid}.`);
        return;
      }

      const result = applyEditsToRow(columns, row, rowEdits);
      if ('validationError' in result) {
        setEditError(result.validationError);
        return;
      }

      if (result.editedRow === row || (await commitRowEdits([result.editedRow]))) {
        clearEditsForRow(row.guid);
      }
    },
    [allRowEdits, clearEditsForRow, columns, commitRowEdits],
  );

  const filterOptionsByColumnKey = useMemo(() => {
    const filterOptions: { [columnKey: string]: readonly string[] } = {};

    for (const column of columns) {
      if (column.filterType !== 'select') continue;

      const values = Array.from(new Set(rows.map((row) => column.getCellValue(row)).filter(Boolean)));
      filterOptions[column.key] = values.toSorted(column.compare ?? ((a, b) => a.localeCompare(b)));
    }

    return filterOptions;
  }, [columns, rows]);

  const setFilter = useCallback((columnKey: string, filterValue: FilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [columnKey]: isActiveFilter(filterValue) ? filterValue : undefined,
    }));
  }, []);

  const toggleSort = useCallback((columnKey: string) => {
    setSortings((prevState) => {
      const prevSortingForColumn = prevState.find((sorting) => sorting.columnKey === columnKey);
      const newSorting: Sorting = { columnKey, direction: prevSortingForColumn?.direction === 'asc' ? 'desc' : 'asc' };
      return [newSorting];
    });
  }, []);

  const setSort = useCallback((columnKey: string, direction: Sorting['direction']) => {
    setSortings([{ columnKey, direction }]);
  }, []);

  const setGroupBy = useCallback((columnKey: string) => {
    setExpandedGroupKeys([]);
    setGroupByColumnKey(columnKey);
  }, []);

  const clearColumn = useCallback((columnKey: string) => {
    setFilters((prevState) => ({ ...prevState, [columnKey]: undefined }));
    setSortings((prevState) => prevState.filter((sorting) => sorting.columnKey !== columnKey));
    setGroupByColumnKey((prevState) => (prevState === columnKey ? '' : prevState));
    setExpandedGroupKeys([]);
  }, []);

  const toggleGroupExpansion = useCallback((groupKey: string) => {
    setExpandedGroupKeys((prevState) =>
      prevState.includes(groupKey)
        ? prevState.filter((expandedGroupKey) => expandedGroupKey !== groupKey)
        : [...prevState, groupKey],
    );
  }, []);

  const updateBulkEdit = useCallback((scopeKey: string, columnKey: string, value: string) => {
    setEditError(null);
    setBulkEdits((prevState) => ({
      ...prevState,
      [scopeKey]: { ...prevState[scopeKey], [columnKey]: value },
    }));
  }, []);

  const clearBulkEdits = useCallback((scopeKey: string) => {
    setBulkEdits((prevState) => ({ ...prevState, [scopeKey]: undefined }));
  }, []);

  const saveBulkEdits = useCallback(
    async (scopeKey: string, rowsToEdit: readonly TRow[]) => {
      const result = getUpdatedRows(columns, rowsToEdit, bulkEdits[scopeKey] ?? {});
      if ('validationError' in result) {
        setEditError(result.validationError);
        return;
      }

      if (result.editedRows.length === 0 || (await commitRowEdits(result.editedRows))) {
        clearBulkEdits(scopeKey);
      }
    },
    [bulkEdits, clearBulkEdits, columns, commitRowEdits],
  );

  const renderBulkEditControls = useCallback(
    (
      scopeKey: string,
      rowsToEdit: readonly TRow[],
      saveLabel: string,
      trailingActions?: React.ReactNode,
      showClearAction = true,
    ) => {
      if (!hasEditPermission || editableColumns.length === 0 || rowsToEdit.length === 0) return null;

      return (
        <BulkEditControls
          editableColumns={editableColumns}
          edits={bulkEdits[scopeKey] ?? {}}
          rowsToEdit={rowsToEdit}
          saveLabel={saveLabel}
          onEditChange={(columnKey, value) => updateBulkEdit(scopeKey, columnKey, value)}
          onSave={() => void saveBulkEdits(scopeKey, rowsToEdit)}
          onClear={() => clearBulkEdits(scopeKey)}
          showClearAction={showClearAction}
          trailingActions={trailingActions}
        />
      );
    },
    [bulkEdits, clearBulkEdits, editableColumns, hasEditPermission, saveBulkEdits, updateBulkEdit],
  );

  const filteredAndSortedRows = useMemo(
    () => filterAndSortRows(rows, columns, filters, sortings),
    [columns, filters, rows, sortings],
  );

  const displayRows = useMemo(
    () => buildDisplayRows(filteredAndSortedRows, columns, groupByColumnKey, expandedGroupKeys),
    [columns, expandedGroupKeys, filteredAndSortedRows, groupByColumnKey],
  );

  // Shift-click ranges follow the visible display order, which differs from the sorted order when
  // grouping is active, and must never include rows hidden inside collapsed groups.
  const visibleRowGuids = useMemo(
    () => displayRows.flatMap((displayRow) => (displayRow.type === 'price' ? [displayRow.row.guid] : [])),
    [displayRows],
  );

  const selectedFilteredRows = useMemo(
    () => filteredAndSortedRows.filter((row) => selectedRowGuids.has(row.guid)),
    [filteredAndSortedRows, selectedRowGuids],
  );
  const selectedRows = useMemo(() => rows.filter((row) => selectedRowGuids.has(row.guid)), [rows, selectedRowGuids]);
  const allFilteredRowsSelected =
    filteredAndSortedRows.length > 0 && selectedFilteredRows.length === filteredAndSortedRows.length;
  const someFilteredRowsSelected = selectedFilteredRows.length > 0;

  const updateRowSelection = useCallback(
    (rowGuid: string, selected: boolean, shiftKey: boolean) => {
      setSelectedRowGuids((prevState) => {
        const selectedGuids = new Set(prevState);
        const rowIndex = visibleRowGuids.indexOf(rowGuid);
        const lastSelectedRowIndex = lastSelectedRowGuid ? visibleRowGuids.indexOf(lastSelectedRowGuid) : -1;

        if (shiftKey && rowIndex >= 0 && lastSelectedRowIndex >= 0) {
          const startIndex = Math.min(rowIndex, lastSelectedRowIndex);
          const endIndex = Math.max(rowIndex, lastSelectedRowIndex);
          const rangeRowGuids = visibleRowGuids.slice(startIndex, endIndex + 1);

          for (const rangeRowGuid of rangeRowGuids) {
            if (selected) selectedGuids.add(rangeRowGuid);
            else selectedGuids.delete(rangeRowGuid);
          }
        } else if (selected) {
          selectedGuids.add(rowGuid);
        } else {
          selectedGuids.delete(rowGuid);
        }

        return selectedGuids;
      });
      setLastSelectedRowGuid(rowGuid);
    },
    [lastSelectedRowGuid, visibleRowGuids],
  );

  const setAllFilteredRowsSelected = useCallback(
    (selected: boolean) => {
      setSelectedRowGuids(selected ? new Set(filteredAndSortedRows.map((row) => row.guid)) : new Set());
      if (!selected) setLastSelectedRowGuid(null);
    },
    [filteredAndSortedRows],
  );

  const selectedRowsScopeKey = useMemo(() => getRowsScopeKey('selected-rows', selectedRows), [selectedRows]);

  const clearSelectedRows = useCallback(() => {
    setSelectedRowGuids(new Set());
    setLastSelectedRowGuid(null);
    clearBulkEdits(selectedRowsScopeKey);
  }, [clearBulkEdits, selectedRowsScopeKey]);

  useEffect(() => {
    const rowGuids = new Set(rows.map((row) => row.guid));
    setSelectedRowGuids((previous) => {
      const existing = new Set([...previous].filter((guid) => rowGuids.has(guid)));
      return existing.size === previous.size ? previous : existing;
    });
  }, [rows]);

  const fixedHeaderContent = useCallback(
    () => (
      <PriceGridHeader
        columns={columns}
        sortings={sortings}
        onToggleSort={toggleSort}
        onSetSort={setSort}
        filters={filters}
        filterOptionsByColumnKey={filterOptionsByColumnKey}
        onFilterChange={setFilter}
        groupByColumnKey={groupByColumnKey}
        onSetGroupBy={setGroupBy}
        onClearColumn={clearColumn}
        hasEditPermission={hasEditPermission}
        allFilteredRowsSelected={allFilteredRowsSelected}
        someFilteredRowsSelected={someFilteredRowsSelected}
        filteredRowCount={filteredAndSortedRows.length}
        onSetAllFilteredRowsSelected={setAllFilteredRowsSelected}
      />
    ),
    [
      allFilteredRowsSelected,
      columns,
      clearColumn,
      filterOptionsByColumnKey,
      filteredAndSortedRows.length,
      filters,
      groupByColumnKey,
      hasEditPermission,
      setAllFilteredRowsSelected,
      setFilter,
      setGroupBy,
      setSort,
      someFilteredRowsSelected,
      sortings,
      toggleSort,
    ],
  );

  const rowContent = useCallback(
    (_index: number, displayRow: DisplayRow<TRow>) => {
      if (displayRow.type === 'group') {
        return (
          <PriceGridGroupRow
            colSpan={columns.length + 2}
            label={displayRow.label}
            rowCount={displayRow.rows.length}
            isExpanded={expandedGroupKeys.includes(displayRow.key)}
            onToggleExpansion={() => toggleGroupExpansion(displayRow.key)}
          >
            {renderBulkEditControls(
              getRowsScopeKey(displayRow.key, displayRow.rows),
              displayRow.rows,
              `Update ${displayRow.rows.length === 1 ? 'price' : 'group'}`,
            )}
          </PriceGridGroupRow>
        );
      }

      const row = displayRow.row;
      return (
        <PriceGridRow
          row={row}
          columns={columns}
          editableColumns={editableColumns}
          hasEditPermission={hasEditPermission}
          isSelected={selectedRowGuids.has(row.guid)}
          rowEdits={allRowEdits[row.guid]}
          onSelect={(selected, shiftKey) => updateRowSelection(row.guid, selected, shiftKey)}
          onStartEditing={() => startEditingRow(row.guid)}
          onEditChange={(columnKey, value) => updateRowEdit(row.guid, columnKey, value)}
          onSave={() => void saveRowEdits(row)}
          onCancelEditing={() => clearEditsForRow(row.guid)}
        />
      );
    },
    [
      allRowEdits,
      clearEditsForRow,
      columns,
      editableColumns,
      expandedGroupKeys,
      hasEditPermission,
      renderBulkEditControls,
      saveRowEdits,
      selectedRowGuids,
      startEditingRow,
      toggleGroupExpansion,
      updateRowEdit,
      updateRowSelection,
    ],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        ...expandToFillParentStyles.containerStyleProps,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        minWidth: 0,
        flex: '1 1 auto',
        pb: isInsertBarOpen || selectedRows.length > 0 ? `${actionBarHeight + 32}px` : 0,
        bgcolor: 'canvas.main',
        backgroundImage: 'none',
      }}
    >
      {editError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {editError}
        </Alert>
      )}
      <Box sx={{ ...expandToFillParentStyles.targetStyleProps, minWidth: 0, width: '100%' }}>
        <TableVirtuoso<DisplayRow<TRow>>
          data={displayRows}
          components={virtuosoTableComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={rowContent}
        />
      </Box>
      {isInsertBarOpen ? (
        insertBar?.(() => setIsInsertBarOpen(false), setActionBarHeight)
      ) : selectedRows.length > 0 ? (
        <PriceActionBar onHeightChange={setActionBarHeight} sx={{ gap: 1 }}>
          <Typography sx={{ flex: '1 1 160px', minWidth: 0, mx: 1 }} variant="body2">
            {selectedRows.length} {selectedRows.length === 1 ? 'price' : 'prices'} selected
          </Typography>
          {renderBulkEditControls(
            selectedRowsScopeKey,
            selectedRows,
            `Update ${selectedRows.length} ${selectedRows.length === 1 ? 'price' : 'prices'}`,
            <Button size="small" sx={{ flex: { xs: '1 1 120px', md: '0 0 auto' } }} onClick={clearSelectedRows}>
              Cancel
            </Button>,
          )}
        </PriceActionBar>
      ) : (
        insertBar && (
          <Button
            color="primary"
            startIcon={<AddIcon />}
            variant="contained"
            sx={{
              bottom: 16,
              position: 'fixed',
              right: 24,
              zIndex: (theme) => theme.zIndex.appBar + 1,
            }}
            onClick={() => setIsInsertBarOpen(true)}
          >
            Add New Price
          </Button>
        )
      )}
    </Paper>
  );
}
